<template>
  <div v-if="componentReady">
    <div
      id="incident-investigation-initial-report-wrapper"
      data-test="outside-vif"
    >
      <button
        id="intialReportToggleButton"
        @click="showInitialReport = !showInitialReport"
      >
        {{ intialReportToggleLabel }}
      </button>
      <fieldset v-if="investigation && showInitialReport">
        <legend>Initial Report</legend>
        <IncidentReportView
          :report-uuid="investigation.field_incident_report.id"
          :show-investigate-button="false"
        />
      </fieldset>
    </div>

    <template v-if="hasErrors">
      <div :key="hasErrors" class="messages error messages--error">
        <b>Please correct the following error(s):</b>
        <ul>
          <li v-for="error in getErrors">
            {{ error }}
          </li>
        </ul>
      </div>
    </template>
    <b-form @submit.prevent="submitEntity">
      <template v-for="group in fieldGroups">
        <field-group
          v-if="
            group.parent_name === '' || group.format_type === 'accordion_item'
          "
          :group-id="group.group_id"
          :group-type="group.format_type"
          :group-label="group.label"
          :group-config="group"
          :key="group.group_id"
          :disabled="isClosed"
        >
          <BlsCodeSelects
            v-if="group.group_id === 'group_inc_invest_bls_code_asgmnt'"
            :key="group.group_id + '_bls_selects'"
            @bls-code-selection="handleBlsCodeSelection($event)"
            :values="selectedBlsCodes"
            :disabled="isClosed"
          />

          <template v-for="childElement in getChildFields(group.children)">
            <field-group
              v-if="isChildNestedGroup(childElement)"
              :key="childElement.group_id + '-' + childElement.label"
              :group-id="childElement.group_id"
              :group-type="childElement.format_type"
              :group-label="childElement.label"
              :group-config="childElement"
              :disabled="isClosed"
            >
              <template
                v-if="
                  childElement.group_id === 'group_corr_prev_actions_listing'
                "
              >
                <corrective-actions-listing
                  :key="childElement.group_id"
                  :investigation-uuid="uuid"
                  :disabled="isClosed"
                />
              </template>

              <template
                v-for="subChildElement in getChildFields(childElement.children)"
              >
                <user-lookup
                  v-if="
                    subChildElement.field_name ===
                    'field_inc_invest_person_involved'
                  "
                  :key="subChildElement.field_name"
                  :field-config="subChildElement"
                  @userLookupSelection="userSelected"
                  ref="user-lookup"
                  :lose-name-contains="true"
                  :value="selectedUser"
                  :disabled="isClosed"
                />

                <user-groups
                  v-else-if="
                    subChildElement.field_name === 'field_inc_invest_group'
                  "
                  :key="subChildElement.field_name"
                  :user-uuid="selectedUser ? selectedUser.id : null"
                  :field-data="subChildElement"
                  :field-value="selectedGroup"
                  label="Group"
                  @userGroupSelected="groupSelected"
                  :disabled="isClosed"
                />

                <entity-form-field
                  v-else-if="
                    subChildElement.field_name ===
                    'field_inc_invest_rep_category'
                  "
                  :key="subChildElement.field_name"
                  :field-data="subChildElement"
                  :entity="entity"
                  :parent-value="formData[subChildElement.field_name]"
                  entity-type="node"
                  bundle="incident_report"
                  :placeholder="
                    EntityFormHelper.getPlaceholder(subChildElement.field_name)
                  "
                  @update-form-data="updateFormData($event)"
                  @change="handleConditionalFields($event)"
                  :disabled="isClosed"
                  :styled="true"
                  :add-class="
                    [
                      'field_inc_invest_area_type_other',
                      'field_inc_invest_job_func_other',
                      'field_inc_inv_serious_inj_other',
                    ].indexOf(subChildElement.field_name > -1)
                      ? 'hasMinWidth'
                      : 'false'
                  "
                />

                <entity-form-field
                  v-else
                  :key="subChildElement.field_name"
                  :field-data="subChildElement"
                  :entity="entity"
                  :parent-value="formData[subChildElement.field_name]"
                  entity-type="node"
                  bundle="incident_report"
                  :no-default-value="
                    subChildElement.field_name ===
                    'field_inc_invest_inj_date_hired'
                  "
                  :placeholder="
                    EntityFormHelper.getPlaceholder(subChildElement.field_name)
                  "
                  @update-form-data="updateFormData($event)"
                  :hidden="isConditionallyHidden(subChildElement.field_name)"
                  :disabled="isClosed"
                  :add-class="
                    [
                      'field_inc_invest_area_type_other',
                      'field_inc_invest_job_func_other',
                      'field_inc_inv_serious_inj_other',
                    ].indexOf(childElement.field_name > -1)
                      ? 'hasMinWidth'
                      : 'false'
                  "
                />
              </template>
            </field-group>

            <template v-else>
              <file-attachment-listing
                :key="childElement.field_name + '-file-listing'"
                v-if="group.group_id === 'group_inc_invest_file_attachment'"
                :files="investigation?.field_inc_invest_documents"
                allow-file-download
                allow-file-remove
                :disabled="isClosed"
              />

              <raft-file-upload
                v-if="childElement.field_name === 'field_inc_invest_documents'"
                :key="childElement.field_name"
                :field-name="childElement.field_name"
                :label="childElement.label"
                :description="childElement.description"
                :field-config="childElement"
                :required="childElement.required"
                @fileUploaded="handleFileSelectionAction($event)"
                :disabled="isClosed"
                :multiple="true"
                suppress-replace-warning
              />
              <entity-form-field
                v-else-if="
                  childElement.field_name === 'field_inc_invest_root_causes'
                "
                :key="childElement.field_name"
                :field-data="childElement"
                :entity="entity"
                :parent-value="parsedRootCauses"
                entity-type="node"
                bundle="incident_report"
                :placeholder="
                  EntityFormHelper.getPlaceholder(childElement.field_name)
                "
                @update-form-data="updateFormData($event)"
                @change="handleConditionalFields($event)"
                :disabled="
                  isClosed &&
                  childElement.field_name !== 'field_inc_invest_report_status'
                "
                :styled="true"
              />
              <entity-form-field
                v-else
                :key="childElement.field_name"
                :field-data="childElement"
                :entity="entity"
                :parent-value="formData[childElement.field_name]"
                entity-type="node"
                bundle="incident_report"
                :placeholder="
                  EntityFormHelper.getPlaceholder(childElement.field_name)
                "
                @update-form-data="updateFormData($event)"
                @change="handleConditionalFields($event)"
                :disabled="
                  isClosed &&
                  childElement.field_name !== 'field_inc_invest_report_status'
                "
                :add-class="
                  [
                    'field_inc_invest_area_type_other',
                    'field_inc_invest_job_func_other',
                    'field_inc_inv_serious_inj_other',
                  ].indexOf(childElement.field_name > -1)
                    ? 'hasMinWidth'
                    : 'false'
                "
              />
            </template>
          </template>
        </field-group>
      </template>
      <b-form-row id="incident-investigation-form-controls">
        <b-button
          type="submit"
          variant="primary"
          :disabled="submitDisabled"
          @click.prevent="handleSubmitAction()"
        >
          Save
        </b-button>
        <b-button
          type="submit"
          variant="primary"
          :disabled="submitDisabled"
          @click.prevent="handleSubmitAndEdit()"
        >
          Save & Continue Editing
        </b-button>
        <b-link
          href="#"
          :disabled="isBusy"
          @click.prevent="handleCancelAction()"
        >
          Cancel
        </b-link>
        <div class="busy-wrapper">
          <raft-busy v-if="isBusy" busy-text="Saving Investigation..." />
        </div>
        <div v-if="isDirty" class="form-data-status">
          * You have unsaved changes to this investigation.
        </div>
      </b-form-row>
    </b-form>
  </div>
</template>

<script>
import axios from "axios";
import BlsCodeSelects from "./BlsCodeSelects.vue";
import CorrectiveActionsListing from "./CorrectiveActionsListing.vue";
import EntityFormField from "../EntityFormField.vue";
import EntityFormHelper from "../../lib/EntityFormHelper";
import FieldGroup from "../raft/FieldGroup.vue";
import FileAttachmentListing from "../FileAttachmentListing.vue";
import IncidentReportView from "./IncidentReportView.vue";
import RaftBusy from "../RaftBusy.vue";
import RaftFileUpload from "../raft/RaftFileUpload.vue";
import raftGenericHelper from "../../lib/raft_generic_helper";
import { restClient, jsonClient } from "../../lib/raftApi";
import UserGroups from "../raft/UserGroups.vue";
import UserLookup from "../raft/UserLookup.vue";

export default {
  name: "IncidentInvestigationForm",
  provide() {
    return {
      isB4: this.$isB4,
    };
  },
  data() {
    return {
      errors: {},
      componentReady: false,
      isDirty: false,
      isBusy: false,
      showInitialReport: false,
      bundleFieldsSorted: [],
      changedData: {},
      currentFiles: [],
      editFields: [],
      entity: {},
      EntityFormHelper: EntityFormHelper,
      fields: {},
      fieldGroups: [],
      formData: {},
      investigation: {},
      selectedUser: { id: null },
      selectedFile: null,
      selectedGroup: null,
      submitDisabled: false,
      redirectToEdit: false,
      userGroups: [],
      selectedBlsCodes: {},
      conditionalFields: {
        field_inc_invest_job_func_other: {
          controllingField: "field_inc_invest_job_function",
          showValues: ["other"],
          state: "hidden",
        },
        field_inc_invest_area_type_other: {
          controllingField: "field_inc_invest_area_type",
          showValues: ["other"],
          state: "hidden",
        },
        field_inc_inv_serious_inj_other: {
          controllingField: "field_inc_invest_rep_category",
          showValues: ["other_serious_injury"],
          state: "hidden",
        },
      },
    };
  },
  props: {
    uuid: { type: String, default: null },
  },
  components: {
    BlsCodeSelects,
    CorrectiveActionsListing,
    EntityFormField,
    FieldGroup,
    FileAttachmentListing,
    IncidentReportView,
    RaftBusy,
    RaftFileUpload,
    UserGroups,
    UserLookup,
  },
  methods: {
    fetchInvestigation: async function () {
      await this.fetchFields();
      const options = {
        path: "node/incident_investigation/" + this.uuid,
        queries: {
          include:
            "field_inc_invest_documents, field_inc_invest_person_involved, field_inc_invest_department_unit,field_inc_invest_root_causes,field_inc_invst_team_assigned",
        },
      };
      const response = await jsonClient
        .get(options)
        .catch((e) => this.errorEmit(e));
      if (response) {
        this.entity = response.data;
        this.investigation = response.deserialized;
        this.selectedUser = this.investigation.field_inc_invest_person_involved;
        this.selectedGroup = this.investigation.field_inc_invest_group
          ? this.investigation.field_inc_invest_group.id
          : null;
        this.investigation.field_inc_invest_documents.forEach((file) => {
          this.currentFiles.push({
            type: "file--file",
            id: file.id,
          });
        });
        this.buildSortedBundleFields();
        this.buildSelectedBlsCodes();
        raftGenericHelper.setPageTitle(
          "Update " + this.entity.attributes.title,
          true,
        );
      }
    },
    handleConditionalFields: function (event) {
      Object.entries(this.conditionalFields).forEach(([key, definition]) => {
        if (event.field_name === definition.controllingField) {
          if (Array.isArray(event.field_value)) {
            if (event.field_value.length) {
              let hideField = true;
              // Look to see if the triggering field value is one of the selected values for the multiselect element.
              event.field_value.forEach((selectedValue) => {
                if (definition.showValues.indexOf(selectedValue) !== -1) {
                  hideField = false;
                }
              });
              if (hideField) {
                this.conditionalFields[key].state = "hidden";
                delete this.formData[key];
                delete this.changedData[key];
              } else {
                this.conditionalFields[key].state = "visible";
              }
            } else {
              // On a multi-select field, removing a single selection results in an empty value array.
              this.conditionalFields[key].state = "hidden";
              delete this.formData[key];
              delete this.changedData[key];
            }
          } else if (definition.showValues.indexOf(event.field_value) !== -1) {
            this.conditionalFields[key].state = "visible";
          } else {
            // The value of the controlling field was changed, so the conditional field should be hidden.  Also,
            // any values in the conditional field are no longer valid.
            this.conditionalFields[key].state = "hidden";
            delete this.formData[key];
            delete this.changedData[key];
          }
        }
      });
    },
    buildSelectedBlsCodes: function () {
      this.investigation.field_bls_codes =
        this.investigation.field_bls_codes || {};
      Object.keys(this.investigation.field_bls_codes).forEach((key) => {
        const codeKey = raftGenericHelper.capitalizeFirstLetter(
          key.substring(0, key.length - 2),
        );
        this.selectedBlsCodes[codeKey] = [];
      });
      Object.entries(this.investigation.field_bls_codes).forEach(
        ([key, value]) => {
          const codeKey = raftGenericHelper.capitalizeFirstLetter(
            key.substring(0, key.length - 2),
          );
          const codeIndex = key.substring(key.length - 1);
          this.selectedBlsCodes[codeKey].push({
            index: codeIndex,
            value: this.investigation.field_bls_codes[key],
          });
        },
      );
    },
    handleCancelAction: function () {
      this.submitDisabled = true;
      window.location.href = "/raft/incident-management/incident-reports";
    },
    handleBlsCodeSelection(selectedCodes) {
      this.updateFormData({
        field_name: "field_bls_codes",
        field_value: {
          ...this.investigation.field_bls_codes,
          ...selectedCodes,
        },
      });
    },
    handleFileSelectionAction(data) {
      this.selectedFile = data.field_value;
      this.updateFormData(data);
    },
    handleSubmitAction() {
      this.submitDisabled = true;
      this.isBusy = true;
      if (this.selectedFile) {
        this.uploadDocumentFiles();
      } else {
        this.updateInvestigation();
      }
    },
    handleSubmitAndEdit() {
      this.redirectToEdit = true;
      this.handleSubmitAction();
    },
    uploadDocumentFiles() {
      const url =
        this.$store.getters.config.baseUrl +
        "/jsonapi/node/incident_investigation/field_inc_invest_documents";
      const headers = {
        "Content-Type": "application/octet-stream",
        Authorization: "Bearer " + this.$store.getters.config.jwt,
        crossDomain: true,
        "X-CSRF-Token": this.$store.getters.config.csrfToken,
        "Content-Disposition": 'file; filename="filename.jpg"',
      };

      const promises = [];
      Object.entries(this.selectedFile).forEach(([index, file]) => {
        file.name
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/"/g, "&quot;");
        headers["Content-Disposition"] = 'file; filename="' + file.name + '"';
        promises.push(axios.post(url, file, { headers: headers }));
      });

      const uploadedFiles = {
        field_name: "field_inc_invest_documents",
        field_value: this.currentFiles,
      };
      axios.all(promises).then(
        (results) => {
          results.forEach((response) => {
            uploadedFiles.field_value.push({
              type: "file--file",
              id: response.data.data.id,
            });
          });
          this.updateFormData(uploadedFiles);
          this.updateInvestigation();
        },
        (error) => {
          this.errorEmit(error.message);
          this.submitDisabled = false;
          this.isBusy = false;
          const message = "Failed to upload the selected document.";
          this.errors.field_inc_invest_documents = [message];
          this.$showNotification("error", message);
        },
      );
    },
    updateInvestigation: async function () {
      const data = EntityFormHelper.createJsonApiEntityData(
        "node",
        "incident_investigation",
      );
      data.id = this.uuid;
      EntityFormHelper.fillJsonApiDataFromFormData(
        data,
        this.bundleFieldsSorted,
        this.changedData,
      );

      // Massage the BLS code data to be the correct format.
      if (
        data.attributes.hasOwnProperty("field_bls_codes") &&
        data.attributes.field_bls_codes.length
      ) {
        data.attributes.field_bls_codes = [
          data.attributes.field_bls_codes[0].value,
        ];
      }

      data.id = this.uuid;

      const options = {
        path: "node/incident_investigation/" + this.uuid,
        queries: {},
        data,
      };
      const response = await jsonClient
        .patch(options)
        .catch((errorMessage, errorData) => {
          this.isBusy = false;
          this.submitDisabled = false;
          const message =
            "An error occurred and the investigation was not saved.";
          this.$showNotification("error", message);
          this.errors.form = [message];
          this.errorEmit(errorMessage, errorData);
        });
      if (response) {
        this.investigation = response.deserialized;
        this.entity = response.data;
        this.$showNotification(
          "success",
          "Successfully updated the incident investigation.",
        );
        setTimeout(() => {
          this.handleRedirect();
        }, 6000);
      }
    },
    fetchFields: async function () {
      const options = {
        path: "fields/node/incident_investigation",
        queries: { entity_uuid: this.uuid },
      };
      const data = await restClient
        .get(options)
        .catch((e) => this.errorEmit(e));
      if (data) {
        Object.entries(data.third_party_settings.default.field_group).forEach(
          ([key, groupData]) => {
            if (groupData.format_type !== "accordion") {
              groupData.group_id = key;
              this.fieldGroups.push(groupData);
            }
          },
        );
        this.fieldGroups = this.fieldGroups.sort((a, b) => a.weight - b.weight);
        delete data.third_party_settings;
        this.fields = data;
      }
    },
    buildSortedBundleFields: function () {
      const bundleFieldsSorted = [];
      Object.values(this.fields).forEach((fieldData) => {
        if (
          fieldData.hasOwnProperty("form_displays") &&
          typeof fieldData.form_displays.default !== "undefined"
        ) {
          bundleFieldsSorted.push(fieldData);
        }
      });
      bundleFieldsSorted.sort(
        (a, b) =>
          a.form_displays.default.weight - b.form_displays.default.weight,
      );
      this.bundleFieldsSorted = bundleFieldsSorted;
      this.setDefaultValues();
    },
    isChildNestedGroup: function (field) {
      return field.hasOwnProperty("children");
    },
    getChildFields: function (fieldGroupChildren) {
      const children = [];
      if (Array.isArray(fieldGroupChildren)) {
        fieldGroupChildren.forEach((fieldName) => {
          const group = this.fieldGroups.find(
            (element) => element.group_id === fieldName,
          );
          if (group) children.push(group);
          const field = this.bundleFieldsSorted.find(
            (element) => element.field_name === fieldName,
          );
          if (field) {
            children.push(field);
          }
        });
      }
      return children;
    },
    setDefaultValues: function () {
      Object.values(this.bundleFieldsSorted).forEach((fieldData) => {
        this.editFields.push(fieldData.field_name);
        let defaultValue = EntityFormHelper.getDefaultValueFromEntity(
          fieldData.field_name,
          fieldData.field_type,
          this.entity,
        );
        if (
          !defaultValue &&
          fieldData.hasOwnProperty("default_value") &&
          fieldData.default_value.length
        ) {
          if (Array.isArray(fieldData.default_value)) {
            defaultValue = [];
            fieldData.default_value.forEach((drupalValue) => {
              if (fieldData.default_value.length > 1) {
                defaultValue.push(drupalValue.value);
              } else {
                defaultValue = drupalValue.value;
              }
            });
          } else {
            defaultValue = fieldData.default_value;
          }
        }

        if (defaultValue) {
          this.updateFormData({
            field_name: fieldData.field_name,
            field_value: defaultValue,
          });
        }
      });
      this.defaultValuesReady = true;
    },
    errorEmit(message, data) {
      console.error(message);
      this.$emit("form-error-message", { message: message, data: data });
    },
    userSelected: function (userObject) {
      const selectedValue = {
        field_name: "field_inc_invest_person_involved",
        field_value: {
          type: userObject.selectedObject.type,
          id: userObject.selectedObject ? userObject.selectedObject.id : null,
        },
      };
      this.selectedUser = userObject.selectedObject;
      this.updateFormData(selectedValue);
    },
    groupSelected: function (groupObject) {
      this.selectedGroup = groupObject.field_value
        ? groupObject.field_value.id
        : null;
      this.updateFormData(groupObject);
    },
    updateFormData(value) {
      switch (value.field_name) {
        case "field_inc_invest_root_causes": {
          // @todo Handle arrays of entity references in entityFormHelper.fillJsonApiDataField.entityReference().
          const newValues = [];
          value.field_value.forEach((cause_id) => {
            newValues.push({
              type: "config_terms_term--config_terms_term",
              id: cause_id,
            });
          });
          value.field_value = newValues;
          break;
        }
        case "field_inc_invest_inj_date_hired": {
          const dateHired = new Date(value.field_value);
          value.field_value =
            raftGenericHelper.formatDateFilterYyyyMmDd(dateHired);
          break;
        }
        default:
          break;
      }
      this.$set(this.formData, value.field_name, value.field_value);
      this.$set(this.changedData, value.field_name, value.field_value);
      this.handleConditionalFields(value);
      this.submitDisabled = false;
      this.isDirty = true;
    },
    handleRedirect: function () {
      if (this.redirectToEdit) {
        window.location.href = `/raft/incident-management/incident-investigation/${this.investigation?.id}/edit`;
      } else if (this.$route.query.destination) {
        window.location.href = this.$route.query.destination;
      } else {
        window.location.href = `/raft/incident-management/incident-investigation/${this.investigation?.id}/view`;
      }
    },
    isConditionallyHidden: function (fieldName) {
      // The field is not hidden if it's not defined as a conditional field.
      if (!this.conditionalFields.hasOwnProperty(fieldName)) {
        return false;
      }
      return this.conditionalFields[fieldName].state === "hidden";
    },
  },
  computed: {
    hasErrors() {
      return this.getErrors?.length;
    },
    intialReportToggleLabel: function () {
      const action = this.showInitialReport ? "Hide" : "Show";
      return action + " Initial Report";
    },
    children: function () {
      return this.fieldGroups.map((g) => g.children).flat();
    },
    getErrors: function () {
      const allErrors = [];
      const fields = Object.keys(this.errors);
      for (let i = 0; i <= fields.length; i++) {
        if (typeof this.errors[fields[i]] !== "undefined") {
          for (let j = 0; j < this.errors[fields[i]].length; j++) {
            allErrors.push(this.errors[fields[i]][j]);
          }
        }
      }
      return allErrors;
    },
    isClosed: function () {
      return this.investigation?.field_inc_invest_report_status === "closed";
    },
    parsedRootCauses() {
      const fieldValue = this.formData?.field_inc_invest_root_causes || [];
      return fieldValue.map((item) => item.id);
    },
  },
  async created() {
    await this.fetchInvestigation();
    this.componentReady = true;
  },
};
</script>

<style scoped>
/* Fix wrap on Initial Details. */
.form-row {
  clear: both;
}

#incident-investigation-initial-report-wrapper {
  margin: 6px 0;
}

#incident-investigation-initial-report-wrapper button {
  float: right;
}

:deep(#raft-js .form-group label#field_inc_invest_group-label) {
  display: block;
}

:deep(.hasMinWidth) {
  min-width: 12rem;
}
</style>
